import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ModalShell from "../modal/ModalShell";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { submitFeedback } from "../../features/feedback/slice-extensions/feedback-slice-extension";
import { selectFeedbackSubmitStatus } from "../../features/feedback/feedback-slice";
import { FeedbackType } from "../../features/feedback/enums/FeedbackType";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";

const MESSAGE_MAX_LENGTH = 4000;

interface FeedbackModalProps {
  onClose: () => void;
}

/** Send us a wish, an issue report, or a general note — from any screen. */
const FeedbackModal: React.FC<FeedbackModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const submitStatus = useAppSelector(selectFeedbackSubmitStatus);

  const [type, setType] = useState<FeedbackType>(FeedbackType.Wish);
  const [message, setMessage] = useState("");

  const busy = submitStatus === LoadingStatus.Loading;
  const canSubmit = message.trim().length > 0 && !busy;

  const typeOptions = [
    { value: FeedbackType.Wish, label: t("feedback.modal.typeWish") },
    { value: FeedbackType.Issue, label: t("feedback.modal.typeIssue") },
    { value: FeedbackType.Other, label: t("feedback.modal.typeOther") },
  ];

  const handleSubmit = async () => {
    try {
      await dispatch(submitFeedback({ type, message: message.trim() })).unwrap();
      toast.success(t("feedback.modal.sent"));
      onClose();
    } catch (error) {
      toast.error((error as { message?: string })?.message || t("feedback.modal.failed"));
    }
  };

  return (
    <ModalShell onClose={onClose} labelledBy="feedback-title">
      <div className="p-6">
        <h2 id="feedback-title" className="text-lg font-bold text-grey-20 dark:text-white">
          {t("feedback.modal.title")}
        </h2>
        <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          {t("feedback.modal.subtitle")}
        </p>

        <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label={t("feedback.modal.typeLabel")}>
          {typeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={type === option.value}
              className={`rounded-full px-4 py-1.5 text-sm font-medium ${
                type === option.value
                  ? "bg-primary-95 text-primary-40 dark:bg-primary-30 dark:text-primary-90"
                  : "text-grey-40 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
              }`}
              onClick={() => setType(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("feedback.modal.messageLabel")}
          <textarea
            className="input mt-1 min-h-[120px]"
            value={message}
            maxLength={MESSAGE_MAX_LENGTH}
            placeholder={t("feedback.modal.messagePlaceholder")}
            onChange={(event) => setMessage(event.target.value)}
          />
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {busy ? t("feedback.modal.sending") : t("feedback.modal.send")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

export default FeedbackModal;
